import React from 'react';
import { usePageContent } from '../services/contentService';
import LoadingOverlay from '../components/LoadingOverlay';
import './YociLabHub.css';
import SEO from '../components/SEO';

const YociLabHub = () => {
  const { content, loading, error } = usePageContent('yocilab-hub');

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0 1rem' : '0 2rem'
  };

  if (loading || !content) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  // Get sections from content
  const heroSection = content?.sections?.find(s => s.type === 'page-hero')?.content;
  const featuresSection = content?.sections?.find(s => s.type === 'features-grid')?.content;

  return (
    <div className="yocilab-hub-page">
      <SEO
        title="YOCILAB Hub"
        description="A creative launchpad for experimentation, innovation, and entrepreneurship. Join our co-working space and incubator programs."
      />
      {/* Hero Section */}
      <div className="hub-hero-section">
        <div style={containerStyle}>
          <div className="hub-section-header">
            <span className="hub-section-label">YOCILAB HUB</span>
          </div>
          <div className="hub-hero-content">
            <div className="hub-image-section">
              <div className="hub-image-overlay-content">
                <h1 className="hub-hero-title">
                  {heroSection?.mainTitle || "Create.\nInnovate. Impact."}
                </h1>
                <button className="hub-cta-button">Join Our Community</button>
              </div>
              <img
                src={require('../assets/YOCILAB/Board room.11ff75e0c48c951770f2.jpg')}
                alt="YOCILAB Hub workspace"
                className="hub-hero-image"
              />
            </div>
            <div className="hub-content-section">
              <p className="hub-main-description">
                {heroSection?.subtitle || "A creative launchpad for experimentation, innovation, and entrepreneurship."}
              </p>
              <p className="hub-detailed-description">
                {heroSection?.description || "YOCILAB Hub is a creative launchpad for experimentation, innovation, and entrepreneurship. Our hubs provide young people with a vibrant community and collaborative space to transform bold ideas into sustainable solutions while reimagining the future of work, technology, and social change in Africa."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Objectives Section */}
      <div className="hub-objectives-section">
        <div style={containerStyle}>
          <div className="objectives-content">
            <div className="objectives-header">
              <span className="section-label-small">OUR OBJECTIVES</span>
              <h2 className="section-title">Building Tomorrow's Leaders</h2>
              <p className="section-description">
                Empowering youth through comprehensive support systems and innovative programs
              </p>
            </div>
            <div className="objectives-grid">
              <div className="objective-item">
                <div className="objective-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M20 7h-3V6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h1v3a1 1 0 0 0 1.8.6l2.7-3.6H14a3 3 0 0 0 3-3V8h3a1 1 0 0 0 0-2zM15 13a1 1 0 0 1-1 1H10.4a1 1 0 0 0-.8.4L8 16.5V15a1 1 0 0 0-1-1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v7z" fill="currentColor" />
                  </svg>
                </div>
                <h3 className="objective-title">Affordable Workspace</h3>
                <p className="objective-description">
                  Provide affordable and accessible workspace for youth innovators, freelancers, and entrepreneurs.
                </p>
              </div>
              <div className="objective-item">
                <div className="objective-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M9.5 7A1.5 1.5 0 1 1 8 5.5 1.5 1.5 0 0 1 9.5 7zm1.5 4.5a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v6h2v-6a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6h2v-6zm8.5-4.5A1.5 1.5 0 1 1 18 5.5 1.5 1.5 0 0 1 19.5 7zm1.5 4.5a3 3 0 0 0-3-3h-3a3 3 0 0 0-3 3v6h2v-6a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6h2v-6z" fill="currentColor" />
                  </svg>
                </div>
                <h3 className="objective-title">Skills Development</h3>
                <p className="objective-description">
                  Deliver cutting-edge digital and innovation training that equips youth with future-ready skills.
                </p>
              </div>
              <div className="objective-item">
                <div className="objective-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3.09 6.26L22 9l-5 4.87 1.18 6.88L12 17.77l-6.18 2.98L7 14.87 2 9l6.91-1.74L12 2z" fill="currentColor" />
                  </svg>
                </div>
                <h3 className="objective-title">Startup Incubation</h3>
                <p className="objective-description">
                  Create a pipeline of youth-led startups and initiatives through structured incubation.
                </p>
              </div>
              <div className="objective-item">
                <div className="objective-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H16c-.8 0-1.54.37-2.01.99l-2.54 3.4c-.74.99-.74 2.31 0 3.3l2.54 3.4c.47.62 1.21.99 2.01.99h2.46L20 22z" fill="currentColor" />
                  </svg>
                </div>
                <h3 className="objective-title">Community Culture</h3>
                <p className="objective-description">
                  Foster a vibrant community culture of collaboration, fun, and lifelong learning.
                </p>
              </div>
              <div className="objective-item">
                <div className="objective-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L23 9l-11-6zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z" fill="currentColor" />
                  </svg>
                </div>
                <h3 className="objective-title">Cross-Sector Partnerships</h3>
                <p className="objective-description">
                  Serve as a meeting point for intergenerational learning and cross-sector partnerships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core Programs Section */}
      <div className="hub-programs-section">
        <div style={containerStyle}>
          <div className="programs-header">
            <span className="section-label-small">CORE PROGRAMS</span>
            <h2 className="section-title">Programs Inside the YOCILAB Hub</h2>
          </div>

          <div className="programs-grid">
            {/* Co-working Space */}
            <div className="program-card">
              <div className="program-image">
                <img
                  src={require('../assets/YOCILAB/Day office.6d707ab2dcc230c554a0 (1).jpg')}
                  alt="Co-working Space"
                />
              </div>
              <div className="program-content">
                <h3 className="program-title">Co-working Space</h3>
                <p className="program-subtitle">A modern, collaborative workspace designed for productivity and connection.</p>
                <p className="program-description">
                  Our Co-working Space offers a vibrant, affordable, and collaborative environment where young
                  changemakers can work, connect, and grow. It's more than a workspace; it's a community that
                  sparks creativity and collaboration.
                </p>
                <div className="program-features">
                  <h4>What you get</h4>
                  <ul>
                    <li>High-speed internet, flexible desks, meeting rooms, quiet corners, and creative zones</li>
                    <li>Membership: flexible passes (daily, weekly, monthly)</li>
                    <li>Value: professional environment + networking opportunities in a youth-friendly setting</li>
                  </ul>
                </div>
                <button className="program-cta-btn">Book a spot</button>
              </div>
            </div>

            {/* TechVibes */}
            <div className="program-card">
              <div className="program-image">
                <img
                  src={require('../assets/YOCILAB/digital tech initiative_051535.JPG')}
                  alt="TechVibes Program"
                />
              </div>
              <div className="program-content">
                <h3 className="program-title">TechVibes</h3>
                <p className="program-subtitle">Fun, hands-on tech workshops for digital empowerment.</p>
                <p className="program-description">
                  A series of fun, hands-on tech workshops that introduce young people to basic computer training,
                  AI, robotics, coding, and essential digital tools. The program promotes intergenerational learning
                  while tackling digital exclusion and poverty.
                </p>
                <div className="program-features">
                  <h4>What you will learn</h4>
                  <ul>
                    <li>Digital Literacy: Basic computer use, internet navigation</li>
                    <li>Coding & Robotics: Programming foundations, building prototypes, AI-powered robotics</li>
                    <li>AI & Future Tech: Generative AI, automation, data science basics</li>
                    <li>Digital Creativity: Graphic design, storytelling, multimedia production</li>
                    <li>Intergenerational Learning – youth teaching parents and community adult's digital skills</li>
                  </ul>
                </div>
                <button className="program-cta-btn">Apply today</button>
              </div>
            </div>

            {/* Incubator */}
            <div className="program-card">
              <div className="program-image">
                <img
                  src={require('../assets/YOCILAB/Entrepreneurship and innovation.jpg')}
                  alt="Incubator Program"
                />
              </div>
              <div className="program-content">
                <h3 className="program-title">Incubator</h3>
                <p className="program-subtitle">Transform bold ideas into sustainable ventures.</p>
                <p className="program-description">
                  Our Incubator helps young innovators turn bold ideas into sustainable ventures. Through mentorship,
                  resources, and hands-on support, we guide startups from concept to launch, empowering youth-led
                  solutions that drive real impact.
                </p>
                <div className="program-features">
                  <h4>What you get</h4>
                  <ul>
                    <li><strong>Components:</strong> mentorship, business modeling, financial literacy, pitching, and seed funding</li>
                    <li><strong>Duration:</strong> 3–6 months per cohort</li>
                    <li><strong>Outcomes:</strong> validated solutions, investor-ready pitches, and scalable ventures</li>
                    <li><strong>Focus:</strong> climate action, digital innovation, education, social impact enterprises</li>
                  </ul>
                </div>
                <button className="program-cta-btn">Learn more</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YociLabHub;